import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Loader2, Stethoscope, ArrowRight, Check, AlertTriangle } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { useClient } from "@/lib/clientContext";
import {
  hasBootstrapped, markBootstrapped, lookupProvider, isResolvedProvider,
  bootstrapTenant, ensureDefaultClient, isPermissionError, DEFAULT_CLIENT_ID,
} from "@/lib/tenantBootstrap";

// First-run onboarding for an empty tenant: prompt for the user's NPI, validate it
// against NPPES, then create the default client (1000) and link the provider to it.
export default function NpiBootstrapModal() {
  const { user } = useAuth();
  const { clients, loading, setSelectedClientId, reload } = useClient();
  const navigate = useNavigate();
  const location = useLocation();

  const [dismissed, setDismissed] = useState(false);
  const [npi, setNpi] = useState("");
  const [phase, setPhase] = useState("prompt"); // prompt | working | error
  const [workingMsg, setWorkingMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [permBlocked, setPermBlocked] = useState(false);
  const runRef = useRef(false);

  const emptyTenant = !loading && Array.isArray(clients) && clients.length === 0;
  const onSetup = location.pathname === "/settings";
  const show = !!user && emptyTenant && !onSetup && !dismissed && !hasBootstrapped(user);

  useEffect(() => { if (!show) { setPhase("prompt"); } }, [show]);

  if (!show) return null;

  const close = () => { markBootstrapped(user); setDismissed(true); };

  const finishToSetup = () => { markBootstrapped(user); setDismissed(true); navigate("/settings", { state: { tab: "practice-setup" } }); };

  const onContinue = async (e) => {
    e?.preventDefault?.();
    if (runRef.current) return;
    const clean = npi.replace(/\D/g, "");
    if (clean.length !== 10) { setErrorMsg("Enter a valid 10-digit NPI."); setPhase("error"); return; }

    runRef.current = true;
    setPhase("working");
    setPermBlocked(false);
    setErrorMsg("");
    try {
      // 1. NPPES gate — a fake / unregistered NPI is rejected here, never added.
      setWorkingMsg("Checking the national NPI registry…");
      let lookupData;
      try {
        lookupData = await lookupProvider(clean);
      } catch (err) {
        if (isPermissionError(err)) throw err;
        lookupData = null;
      }
      if (!isResolvedProvider(lookupData)) {
        runRef.current = false;
        setErrorMsg(
          "That NPI isn’t in the national NPPES registry. Double-check the 10-digit number — only a real, registered provider can be added.",
        );
        setPhase("error");
        return;
      }

      // 2. Provision: default client → add provider → link.
      setWorkingMsg(`Setting up ${lookupData.provider_name || "your provider"}…`);
      const clientId = await bootstrapTenant({ npi: clean, lookupData, clientId: DEFAULT_CLIENT_ID });

      await reload();
      setSelectedClientId(clientId);
      markBootstrapped(user);
      setDismissed(true);
    } catch (err) {
      runRef.current = false;
      if (isPermissionError(err)) {
        setPermBlocked(true);
        setErrorMsg("This account doesn’t have permission to set up clients automatically. You can finish setup manually.");
      } else {
        setErrorMsg("Something went wrong while setting up your tenant. You can retry, or finish setup manually.");
      }
      setPhase("error");
    }
  };

  const onSkip = async () => {
    // Never leave the tenant empty — create the default client best-effort, unlinked.
    try { await ensureDefaultClient(DEFAULT_CLIENT_ID); await reload(); setSelectedClientId(DEFAULT_CLIENT_ID); }
    catch { /* no create rights → they'll finish in Setup */ }
    close();
  };

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center p-4"
      style={{ background: "rgba(15,23,42,.42)", backdropFilter: "blur(6px)" }}
      data-testid="npi-bootstrap-modal">
      <div className="w-full max-w-[460px] rounded-[16px] bg-white shadow-[0_2px_6px_rgba(15,23,42,.07),0_16px_40px_rgba(15,23,42,.16)] overflow-hidden"
        style={{ fontFamily: "'Inter',system-ui,sans-serif" }}>
        <div className="px-6 pt-6 pb-5">
          <div className="w-10 h-10 rounded-[10px] bg-[var(--ink,#0F172A)] text-white flex items-center justify-center mb-4">
            <Stethoscope size={18} strokeWidth={1.9} />
          </div>

          {phase === "working" ? (
            <div className="py-2" data-testid="npi-bootstrap-working">
              <div className="text-[17px] font-extrabold text-[var(--ink,#0F172A)]">Setting up your tenant…</div>
              <div className="mt-4 flex items-center gap-2.5 text-[13px] text-[var(--gray2,#475569)]">
                <Loader2 size={16} className="animate-spin" /> {workingMsg}
              </div>
            </div>
          ) : phase === "error" ? (
            <div data-testid="npi-bootstrap-error">
              <div className="text-[17px] font-extrabold text-[var(--ink,#0F172A)]">Let’s fix that</div>
              <div className="mt-2.5 flex items-start gap-2.5 rounded-[10px] px-3 py-2.5"
                style={{ background: "rgba(240,140,0,.08)" }}>
                <AlertTriangle size={15} className="mt-0.5 flex-shrink-0" style={{ color: "#FF9500" }} />
                <div className="text-[12.5px] leading-[1.5] text-[var(--ink2,#1f2937)]">{errorMsg}</div>
              </div>
              <div className="mt-5 flex items-center gap-2.5">
                {!permBlocked && (
                  <button onClick={() => { setPhase("prompt"); }} className="btn-ink inline-flex items-center gap-1.5"
                    data-testid="npi-bootstrap-retry">
                    Try again
                  </button>
                )}
                <button onClick={finishToSetup}
                  className="text-[13px] font-semibold text-[var(--ink,#0F172A)] underline underline-offset-2 hover:opacity-70"
                  data-testid="npi-bootstrap-setup-link">
                  Finish in Setup
                </button>
              </div>
            </div>
          ) : (
            <form onSubmit={onContinue} data-testid="npi-bootstrap-prompt">
              <div className="text-[19px] font-extrabold text-[var(--ink,#0F172A)] leading-tight">Let’s set up your billing tenant</div>
              <div className="mt-1.5 text-[13px] text-[var(--gray2,#475569)] leading-[1.55]">
                Enter your 10-digit NPI. We’ll verify it against the national NPPES registry, then create your first client
                (<span className="font-mono font-semibold">{DEFAULT_CLIENT_ID}</span>) and link your provider to it.
              </div>

              <label className="block mt-5 text-[10px] font-bold tracking-[.08em] uppercase text-[var(--gray3,#64748b)]" htmlFor="npi-bootstrap-input">
                NPI number
              </label>
              <input
                id="npi-bootstrap-input"
                data-testid="npi-bootstrap-input"
                className="lf-input mt-1 w-full font-mono tracking-wide"
                inputMode="numeric"
                maxLength={12}
                placeholder="e.g. 1780904094"
                value={npi}
                onChange={(e) => setNpi(e.target.value)}
                autoFocus
              />

              <div className="mt-5 flex items-center justify-between">
                <button type="button" onClick={onSkip}
                  className="text-[12.5px] font-semibold text-[var(--gray2,#475569)] hover:text-[var(--ink,#0F172A)]"
                  data-testid="npi-bootstrap-skip">
                  I’ll do this later
                </button>
                <button type="submit" className="btn-ink inline-flex items-center gap-1.5"
                  data-testid="npi-bootstrap-continue">
                  Continue <ArrowRight size={14} />
                </button>
              </div>

              <div className="mt-4 flex items-center gap-1.5 text-[11px] text-[var(--gray3,#64748b)]">
                <Check size={12} /> Only a real, registered provider can be added — no fake NPIs.
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
